import {ChangeDetectionStrategy, Component} from '@angular/core';
import {TableCardComponent} from '@aviary/components/table-card/table-card.component';
import {ColumnDefinition, RouterActions, TableCardDefinition} from '@store/aviary/aviary.adapter';
import {ActionIconButtonDefinition, ALWAYS, CURRENT_ROW_OBJECT, LoadableSelectorContainer} from '@aviary/components/table/table.component.types';
import {DISPLAYABLE_ITEM_REQUEST_STATUS, ITEM_REQUEST_STATUS_CHIP_COLORS, ItemRequest} from '@store/item-requests/item-requests.types';
import {ItemRequestsSelectors} from '@store/item-requests/item-requests.selectors';
import {createStringSelector} from '@aviary/store/store.helpers';
import {MelLinkComponent} from '@lib/components/mel-link/mel-link.component';
import {ItemRequestTitleComponent} from '@app/my-account/item-requests/table-column-components/item-request-title.component';
import {ItemRequestAuthorColumnComponent} from '@app/my-account/item-requests/table-column-components/item-request-author-column.component';
import {CombinedTitleAuthorColumnComponent} from '@app/my-account/item-requests/table-column-components/combined-title-author-column.component';
import {CombinedStatusActionsColumnComponent} from '@app/my-account/item-requests/table-column-components/combined-status-actions-column.component';
import {MxSelector, MxSelectorCreator} from '@aviary/store/store.types';
import {createSelector} from '@ngrx/store';
import {ViewportSelectors} from '@aviary/store/viewport/viewport.selectors';
import {ItemRequestsActions} from '@store/item-requests/item-requests.actions';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {HoldsSelectors} from '@store/holds/holds.selectors';
import {HoldsActions} from '@store/holds/holds.actions';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {HiddenUntilLoadedDirective} from '@aviary/directives/hidden-until-loaded.directive';
import {alwaysFalse} from '@aviary/store/common/common.selectors';
import {isPopulatedArray} from '@aviary/store/common/typing.helpers';

type ItemRequestColumnDefinition<SelectorContainer extends LoadableSelectorContainer> = ColumnDefinition<ItemRequest, SelectorContainer>;
type ItemRequestActionButtonDefinition<SelectorContainer extends LoadableSelectorContainer> = ActionIconButtonDefinition<ItemRequest, SelectorContainer>;

@Component({
  selector: 'px-item-requests',
  host: {
    class: 'flex flex-col gap-y-7 lg:pe-4',
  },
  template: `
    @let activeRequestsLoaded = activeSelectors.finishedLoading | select:false;
    @let closedRequestsLoaded = closedSelectors.finishedLoading | select:false;
    @let holdsLoaded = holdSelectors.finishedLoading | select:false;
    <mx-table-card mxHiddenUntilLoaded [reveal]="activeRequestsLoaded" [definition]="activeRequestsCardDefinition">
      <px-mel-link footer introText="Did you check the ILL?"/>
    </mx-table-card>

    <mx-table-card mxHiddenUntilLoaded [reveal]="closedRequestsLoaded && holdsLoaded" [definition]="closedRequestsCardDefinition"/>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TableCardComponent, MelLinkComponent, SelectPipe, HiddenUntilLoadedDirective]
})
export class ItemRequestsComponent {
  public readonly activeSelectors = ItemRequestsSelectors.ActiveItemRequests;
  public readonly closedSelectors = ItemRequestsSelectors.ClosedItemRequests;
  public readonly holdSelectors = HoldsSelectors.HoldsForCatalogRecords;
  private readonly statelessSelectors = ItemRequestsSelectors.Stateless;
  private readonly patronSelectors = PatronSelectors.AccountStatus;

  public readonly headerTextActive: MxSelector<string> =
    createSelector(
      ViewportSelectors.isMdAndUp,
      (isMdAndUp) => `Active${isMdAndUp ? ' Requests' : ''}`
    );

  public readonly headerTextClosed: MxSelector<string> =
    createSelector(
      ViewportSelectors.isMdAndUp,
      (isMdAndUp) => `Closed${isMdAndUp ? ' Requests' : ''}`
    );

  // Extend the "Show Add Hold Icon" selector to include a check for the presence of a linked catalog record.
  private readonly showAddHoldIconFor: MxSelectorCreator<[ItemRequest], boolean> =
    (request) => createSelector(
      this.holdSelectors.showAddHoldIconFor(request.linkedCatalogRecord?.id ?? 0),
      this.statelessSelectors.hasLinkedCatalogRecordFor(request),
      (showAddHold, hasLinkedCatalogRecord) => showAddHold && hasLinkedCatalogRecord
    );

  private readonly hideAddHoldIconFor: MxSelectorCreator<[ItemRequest], boolean> =
    (request) => createSelector(
      this.showAddHoldIconFor(request),
      (show) => !show
    );

  private readonly enableAddHoldIconFor: MxSelectorCreator<[ItemRequest], boolean> =
    (request) => createSelector(
      this.showAddHoldIconFor(request),
      this.patronSelectors.hasBlocks,
      (showAddHold, hasBlocks) => showAddHold && !hasBlocks
    );

  private readonly addHoldTooltipFor: MxSelectorCreator<[ItemRequest], string> =
    (request) => createSelector(
      this.enableAddHoldIconFor(request),
      (enabled): string => enabled ? 'Place Hold' : 'Cannot place hold due to blocks on your account.'
    );

  // For placeholder hold button, to take up action space when we haven't figured out the state of the circ item's hold button yet on load.
  private readonly showHoldPlaceholderIconFor: MxSelectorCreator<[ItemRequest], boolean> =
    (request) => createSelector(
      this.holdSelectors.hideEditHoldIconFor(request.linkedCatalogRecord?.id ?? 0),
      this.holdSelectors.hideViewHoldsIconFor(request.linkedCatalogRecord?.id ?? 0),
      this.hideAddHoldIconFor(request),
      (hideEdit, hideView, hideAdd) => hideEdit && hideView && hideAdd
    );

  public readonly activeRequestsCardDefinition: TableCardDefinition<ItemRequest, typeof ItemRequestsSelectors.ActiveItemRequests> = {
    leftHeaderContent: {
      value: this.headerTextActive,
      count: ItemRequestsSelectors.ActiveItemRequests.requestCount,
      cssClasses: 'text-headline-small lg:text-headline-medium'
    },
    actions: [
      {
        label: 'Request an Item',
        clickActionCreator: ItemRequestsActions.showCreateItemRequest,
        enabled: createSelector(
          this.patronSelectors.loaded,
          this.patronSelectors.hasBlocks,
          this.patronSelectors.underItemRequestLimit,
          (loaded, hasBlocks, underLimit) => loaded && !hasBlocks && underLimit
        ),
        tooltip: createStringSelector(
          this.patronSelectors.loaded,
          this.patronSelectors.blocks,
          this.patronSelectors.underItemRequestLimit,
          this.patronSelectors.monthlyItemRequestLimit,
          (loaded, blocks, underLimit, limit) => {
            if (!loaded) return 'Your account status is currently unavailable.';
            if (blocks.includes('MAX_FEE_LIMIT')) return 'Item requests are unavailable due to your account balance.';
            if (isPopulatedArray(blocks)) return 'Item requests are unavailable due to a block on your account.';
            if (underLimit) return '';
            return limit === 0
              ? 'Item requests are not available for your account.'
              : `You’ve reached your monthly limit of ${limit} item requests.`;
          }
        )
      }
    ],
    table: {
      defaultSelectorContainer: ItemRequestsSelectors.ActiveItemRequests,
      dataSource: 'activeItemRequests',
      includePaginationControls: false,
      responsiveColumnsDefinition: {
        columnNamesSm: ['combinedTitleAndAuthor', 'combinedStatusAndActions'],
        columnNamesMd: ['date', 'combinedTitleAndAuthor', 'status'],
        columnNamesLg: ['date', 'item', 'author', 'status'],
        omitActionsSm: true,
        responsiveHideHeaderRow: true
      },
      columns: this.defineColumns<typeof ItemRequestsSelectors.ActiveItemRequests>(false),
      actionIconButtons: [
        this.defineViewDetailsButton<typeof ItemRequestsSelectors.ActiveItemRequests>(),
        {
          iconName: 'close',
          tooltip: 'Cancel Request',
          cssClasses: {'material-icons-outlined': ALWAYS},
          clickActionCreator: ItemRequestsActions.showCancelItemRequest
        }
      ]
    }
  };

  public readonly closedRequestsCardDefinition: TableCardDefinition<ItemRequest, typeof ItemRequestsSelectors.ClosedItemRequests> = {
    leftHeaderContent: {
      value: this.headerTextClosed,
      count: ItemRequestsSelectors.ClosedItemRequests.requestCount,
      cssClasses: 'text-headline-small lg:text-headline-medium'
    },
    table: {
      defaultSelectorContainer: ItemRequestsSelectors.ClosedItemRequests,
      dataSource: 'closedItemRequests',
      responsiveColumnsDefinition: {
        columnNamesSm: ['combinedTitleAndAuthor', 'combinedStatusAndActions'],
        columnNamesMd: ['date', 'combinedTitleAndAuthor', 'status'],
        columnNamesLg: ['date', 'item', 'author', 'status'],
        omitActionsSm: true,
        responsiveHideHeaderRow: true
      },
      columns: this.defineColumns<typeof ItemRequestsSelectors.ClosedItemRequests>(true),
      actionIconButtons: [
        // Only ONE of the following hold-related action icons can be visible on the row at any given time.
        {
          iconName: 'bookmark',
          tooltip: (request) => this.holdSelectors.editHoldTooltip(request.linkedCatalogRecord?.id ?? 0),
          cssClasses: { 'text-primary': ALWAYS },
          visible: (request) => this.holdSelectors.showEditHoldIconFor(request.linkedCatalogRecord?.id ?? 0),
          enabled: (request) => this.holdSelectors.singleHoldIsEditable(request.linkedCatalogRecord?.id ?? 0),
          clickActionCreator: (request) => HoldsActions.editHoldByCatalogRecordId(request.linkedCatalogRecord?.id ?? 0)
        },
        {
          iconName: 'bookmark',
          tooltip: 'View Holds',
          cssClasses: { 'text-primary': ALWAYS },
          visible: (request) => this.holdSelectors.showViewHoldsIconFor(request.linkedCatalogRecord?.id ?? 0),
          clickActionCreator: () => RouterActions.navigateTo('HOLDS')
        },
        {
          iconName: 'bookmark_add',
          tooltip: this.addHoldTooltipFor,
          cssClasses: {
            'material-icons-outlined': ALWAYS,
            'text-on-surface-variant': ALWAYS
          },
          visible: this.showAddHoldIconFor,
          enabled: this.enableAddHoldIconFor,
          clickActionCreator: (request) => HoldsActions.placeHoldByCatalogRecordId(request.linkedCatalogRecord?.id ?? 0)
        },

        // This action icon is pure trickery - it doesn't do anything and will be hidden once the rest of the hold icon selectors are settled.  This stops the
        // Actions column from collapsing when no second action is visible in the row on the initial table load.
        {
          iconName: 'bookmark_border',
          visible: this.showHoldPlaceholderIconFor,
          cssClasses: { 'opacity-[0.001]': ALWAYS },
          enabled: () => alwaysFalse
        },

        // View Details button
        this.defineViewDetailsButton<typeof ItemRequestsSelectors.ClosedItemRequests>()
      ]
    }
  };

  private defineColumns<SelectorContainer extends LoadableSelectorContainer>(closedRequests: boolean): ItemRequestColumnDefinition<SelectorContainer>[] {
    return [
      {
        type: 'text',
        value: ItemRequestsSelectors.Stateless.createdFor,
        header: 'Date',
        format: 'DATE_ONLY',
        headerCssClasses: 'w-[7rem]',
        overrideColumnName: 'date'
      },
      {
        type: 'component',
        columnName: 'item',
        componentClass: ItemRequestTitleComponent,
        componentProperties: {
          itemRequest: CURRENT_ROW_OBJECT,
          catalogRecord: ItemRequestsSelectors.Stateless.linkedCatalogRecordFor
        }
      },
      {
        type: 'component',
        columnName: 'author',
        componentClass: ItemRequestAuthorColumnComponent,
        componentProperties: {
          itemRequest: CURRENT_ROW_OBJECT
        },
        headerCssClasses: 'w-[20%]',
      },
      {
        type: 'component',
        columnName: 'combinedTitleAndAuthor',
        header: 'Item',
        componentClass: CombinedTitleAuthorColumnComponent,
        componentProperties: {
          itemRequest: CURRENT_ROW_OBJECT,
          catalogRecord: ItemRequestsSelectors.Stateless.linkedCatalogRecordFor
        }
      },
      {
        type: 'statusChip',
        value: ItemRequestsSelectors.Stateless.statusFor,
        header: 'Status',
        overrideColumnName: 'status',
        headerCssClasses: 'w-[9rem]',
        statusChipColors: ITEM_REQUEST_STATUS_CHIP_COLORS,
        displayableStatusNames: DISPLAYABLE_ITEM_REQUEST_STATUS
      },
      {
        type: 'component',
        columnName: 'combinedStatusAndActions',
        componentClass: CombinedStatusActionsColumnComponent,
        componentProperties: {
          itemRequest: CURRENT_ROW_OBJECT,
          isClosed: closedRequests
        },
        headerCssClasses: 'w-[9rem]',
      },
    ];
  }

  private defineViewDetailsButton<SelectorContainer extends LoadableSelectorContainer>(): ItemRequestActionButtonDefinition<SelectorContainer> {
    return {
      iconName: 'comment',
      tooltip: 'View Details',
      cssClasses: {'material-icons-outlined': ALWAYS},
      badge: itemRequest => createStringSelector(
        ItemRequestsSelectors.Stateless.hasUnreadStaffCommentsFor(itemRequest),
        hasComments => hasComments ? '!' : ''
      ),
      clickActionCreator: (itemRequest) => RouterActions.navigateTo('ITEM_REQUEST_DETAILS', {id: itemRequest.id})
    };
  }
}
